/**
 * Two-Factor Authentication Challenge Component
 *
 * ISO 27001 A.9.4.2 - Secure log-on procedures
 * Composant de vérification 2FA lors du login
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Shield,
  Smartphone,
  Key,
  Loader2,
  AlertCircle,
  ArrowLeft,
  HelpCircle,
} from 'lucide-react';
import { twoFactorApi } from '../api/two-factor.api';

export interface TwoFactorChallengeProps {
  /** Token de challenge reçu lors du login */
  challengeToken: string;
  /** Méthodes 2FA disponibles */
  methods: string[];
  /** Callback après vérification réussie */
  onSuccess: (response: {
    accessToken: string;
    refreshToken: string;
    user: any;
    expiresIn: number;
  }) => void;
  /** Callback pour retourner au login */
  onCancel: () => void;
  /** Classe CSS additionnelle */
  className?: string;
}

export const TwoFactorChallenge: React.FC<TwoFactorChallengeProps> = ({
  challengeToken,
  methods: _methods, // Reserved for future use: display available 2FA methods (TOTP, SMS, etc.)
  onSuccess,
  onCancel,
  className = '',
}) => {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [useBackup, setUseBackup] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleVerify = async () => {
    if (useBackup ? code.length < 8 : code.length !== 6) {
      setError(useBackup
        ? t('auth.twoFactor.errorBackupLength')
        : t('auth.twoFactor.errorCodeLength')
      );
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const response = await twoFactorApi.verify2FA(challengeToken, code, useBackup);

      if (response.success && response.accessToken) {
        onSuccess({
          accessToken: response.accessToken,
          refreshToken: response.refreshToken || '',
          user: response.user,
          expiresIn: 900, // Default 15 minutes
        });
      } else {
        setError(response.message || t('auth.twoFactorSetup.invalidCode'));
      }
    } catch (err: any) {
      setError(err.message || t('auth.twoFactor.verifying'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading) {
      handleVerify();
    }
  };

  const inputClasses = `w-full px-4 py-4 text-2xl text-center font-mono tracking-[0.5em]
    bg-white/80 backdrop-blur-sm border-2 rounded-2xl transition-all duration-300 outline-none
    focus:border-teal-500 focus:bg-white focus:shadow-lg focus:shadow-teal-500/10
    border-gray-200 hover:border-teal-300
    placeholder:text-gray-300 placeholder:tracking-normal placeholder:text-base`;

  return (
    <div className={`w-full max-w-md ${className}`}>
      {/* Header */}
      <div className="text-center mb-8">
        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center shadow-lg shadow-teal-500/30">
          <Shield className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2">
          {t('auth.twoFactor.title')}
        </h2>
        <p className="text-gray-500">
          {useBackup
            ? t('auth.twoFactor.subtitleBackup')
            : t('auth.twoFactor.subtitleApp')
          }
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-2xl text-red-600 text-sm flex items-start gap-3">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {/* Code Input */}
      <div className="mb-6">
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          {useBackup ? t('auth.twoFactor.backupCodeLabel') : t('auth.twoFactor.sixDigitLabel')}
        </label>
        <div className="relative">
          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
            {useBackup ? <Key size={20} /> : <Smartphone size={20} />}
          </div>
          <input
            type="text"
            value={code}
            onChange={(e) => {
              const value = useBackup
                ? e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8)
                : e.target.value.replace(/\D/g, '').slice(0, 6);
              setCode(value);
              setError(null);
            }}
            onKeyPress={handleKeyPress}
            className={`${inputClasses} pl-12`}
            placeholder={useBackup ? 'XXXXXXXX' : '000000'}
            autoFocus
            autoComplete="one-time-code"
          />
        </div>
      </div>

      {/* Verify Button */}
      <button
        onClick={handleVerify}
        disabled={isLoading || (useBackup ? code.length < 8 : code.length !== 6)}
        className="w-full py-4 bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-white font-bold text-base rounded-2xl transition-all duration-300 disabled:opacity-50 shadow-lg shadow-teal-500/30 hover:shadow-xl hover:shadow-teal-500/40 flex items-center justify-center gap-3"
      >
        {isLoading ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>{t('auth.twoFactor.verifying')}</span>
          </>
        ) : (
          <>
            <Shield className="w-5 h-5" />
            <span>{t('auth.twoFactor.verify')}</span>
          </>
        )}
      </button>

      {/* Toggle Backup Code */}
      <div className="mt-6 text-center">
        <button
          onClick={() => {
            setUseBackup(!useBackup);
            setCode('');
            setError(null);
          }}
          className="text-sm text-teal-600 hover:text-teal-700 font-medium flex items-center gap-2 mx-auto"
        >
          <HelpCircle className="w-4 h-4" />
          {useBackup
            ? t('auth.twoFactor.useApp')
            : t('auth.twoFactor.useBackup')
          }
        </button>
      </div>

      {/* Back to Login */}
      <button
        onClick={onCancel}
        className="w-full mt-4 py-4 border-2 border-gray-200 text-gray-700 font-semibold rounded-2xl hover:border-teal-500 hover:text-teal-600 hover:bg-teal-50/50 transition-all duration-300 flex items-center justify-center gap-2"
      >
        <ArrowLeft className="w-4 h-4" />
        {t('auth.twoFactor.backToLogin')}
      </button>

      {/* Info Box */}
      <div className="mt-8 p-4 bg-teal-50 border border-teal-100 rounded-2xl">
        <div className="flex items-start gap-3">
          <Smartphone className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-teal-700">
            <p className="font-medium mb-1">
              {useBackup ? t('auth.twoFactor.backupCodesInfoTitle') : t('auth.twoFactor.appInfoTitle')}
            </p>
            <p className="text-teal-600 text-xs">
              {useBackup
                ? t('auth.twoFactor.backupCodesInfoText')
                : t('auth.twoFactor.appInfoText')
              }
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TwoFactorChallenge;
